import { difference } from 'lodash';
import { useEffect } from 'react';
import { UseFormReset } from 'react-hook-form';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useRecordNotFoundHandler } from '@/hooks/useRecordNotFoundHandler';
import { useReportError } from '@/hooks/useReportError';
import { UpdateCollaboratorByIdInput } from '@/types/schema';

import { CollaboratorFormShape } from './CollaboratorModal.constants';
import {
  CollaboratorModal_CollaboratorFragment,
  useGetCollaboratorLazyQuery,
} from './graphql/CollaboratorModal.generated';

export function getFormValuesFromNode(
  data: CollaboratorModal_CollaboratorFragment
): CollaboratorFormShape & { _originalRoleId: string } {
  const householdIds = data.households?.map((household) => household.id) ?? [];
  // Get the first role ID if there are roles assigned. This should be okay because
  // there should only be one role per collaborator, even though the data model allows
  // for more.
  const roleId = data?.roles?.[0]?.id ?? '';

  return {
    firstName: data.firstName,
    lastName: data.lastName,
    email: data.user?.email ?? '',
    role: data.kind ?? '',
    roleId: roleId,
    organization: data.organizationName ?? '',
    title: data.title ?? '',
    associatedClientIds: householdIds,
    _previousHouseholdIds: householdIds,
    _originalRoleId: roleId,
  };
}

function getCurrentHouseholdIds(
  formValues: CollaboratorFormShape,
  canViewHouseholds: boolean
): string[] {
  if (!canViewHouseholds) {
    return [];
  }

  return formValues.associatedClientIds;
}

export function mapFormDataToUpdateInput(
  formValues: CollaboratorFormShape,
  collaboratorId: string,
  canViewHouseholds: boolean = true
): UpdateCollaboratorByIdInput {
  const currentHouseholdIds = getCurrentHouseholdIds(
    formValues,
    canViewHouseholds
  );
  const previousHouseholdIds = canViewHouseholds
    ? formValues._previousHouseholdIds
    : [];

  const addHouseholdIDs = difference(currentHouseholdIds, previousHouseholdIds);
  const removeHouseholdIDs = difference(
    previousHouseholdIds,
    currentHouseholdIds
  );

  // Handle role changes
  const originalRoleId = formValues._originalRoleId;
  const newRoleId = formValues.roleId;
  const roleUpdateProps: {
    addRoleIDs?: string[];
    removeRoleIDs?: string[];
    clearRoles?: boolean;
  } = {};

  if (originalRoleId && newRoleId && originalRoleId !== newRoleId) {
    // Changing from one role to another
    roleUpdateProps.removeRoleIDs = [originalRoleId];
    roleUpdateProps.addRoleIDs = [newRoleId];
  } else if (originalRoleId && !newRoleId) {
    // Removing current role
    roleUpdateProps.removeRoleIDs = [originalRoleId];
  } else if (!originalRoleId && newRoleId) {
    // This should probably never happen, but better safe than sorry.
    roleUpdateProps.addRoleIDs = [newRoleId];
  }

  return {
    id: collaboratorId,
    update: {
      firstName: formValues.firstName,
      lastName: formValues.lastName,
      kind: formValues.role || null,
      ...(addHouseholdIDs.length > 0 && { addHouseholdIDs }),
      ...(removeHouseholdIDs.length > 0 && { removeHouseholdIDs }),
      ...roleUpdateProps,
      ...(formValues.title
        ? {
            title: formValues.title,
          }
        : {
            clearTitle: true,
          }),
      ...(formValues.organization
        ? {
            organizationName: formValues.organization ?? null,
          }
        : {
            clearOrganizationName: true,
          }),
    },
  };
}

export function useSyncCollaboratorDataToForm(
  reset: UseFormReset<CollaboratorFormShape>,
  collaboratorId: string,
  onNotFound: () => void
) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const { recoverExpectedNotFound, recoverMissingRecord } =
    useRecordNotFoundHandler({
      message: 'This collaborator is no longer available.',
      onNotFound,
    });
  const [getCollaborator, queryProps] = useGetCollaboratorLazyQuery({
    variables: { collaboratorId: collaboratorId },
    onError: (err) => {
      if (recoverExpectedNotFound(err)) {
        return;
      }

      showFeedback(
        "We weren't able to get the collaborator information. Please refresh the page and try again."
      );
      reportError('CollaboratorEditModal: Could not get collaborator', err, {
        collaboratorId,
      });
    },
  });

  useEffect(() => {
    if (!collaboratorId) {
      return;
    }

    async function syncCollaboratorDataToForm() {
      const { data } = await getCollaborator();
      if (data?.node?.__typename === 'Collaborator') {
        reset(getFormValuesFromNode(data.node));
      } else if (data) {
        recoverMissingRecord();
      }
    }

    void syncCollaboratorDataToForm();
  }, [reset, getCollaborator, collaboratorId, recoverMissingRecord]);

  return queryProps;
}
